import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Fade,
  CircularProgress,
  IconButton,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { globalConfig } from '../configuration/config';
import mainIcon from '../images/main_icon.jpg';
import { useTranslation } from 'react-i18next';
import {
  activateConverterTargetCompany,
  buildConverterDebugEntry,
  buildConverterDebugErrorPayload,
  buildConverterRetryScaleSettings,
  buildConverterErrorMessage,
  buildConverterScreenshotOptions,
  clearGlbPendingRun,
  createChatProjectFromDisplayTape,
  createDisplayTapeFromCalculation,
  createGlbConversionJob,
  CONVERTER_STATUS_DELAY_WARNING_MS,
  CUSTOM_GLB_CONVERTER_MODEL_SCALE,
  DEFAULT_GLB_CONVERTER_COEFFICIENT_SCALE,
  DEFAULT_GLB_CONVERTER_SCALE_MODE,
  downloadStoredGlbSourceFile,
  downloadStoredModelTransformReport,
  extractLegacyCalculationGuid,
  GLB_CONVERTER_MAX_UPLOAD_SIZE_MB,
  GLB_CONVERTER_MODEL_SCALE_PRESETS,
  GLB_CONVERTER_SCALE_MODES,
  GLB_PENDING_RUN_PHASES,
  getDefaultModelName,
  getImportModelsSelectionState,
  pollGlbConversionJob,
  readAccessTokenFromStorage,
  readGlbPendingRun,
  resolveGlbConversionAuditCalculationGuid,
  resolveConverterScaleRequest,
  resolveConverterRedirectTarget,
  sendGlbConversionRetryActionMessage,
  sendGlbSourceLinkFallbackMessage,
  sendGlbSourceLinkMessage,
  writeGlbPendingRun,
} from './import-models-converter';
import { sendBestEffortConversionAuditMessage } from './conversion-audit-message';
import ConverterJobRunPanel from './ConverterJobRunPanel';
import { getGlbJobStatusText, getGlbJobWaitDuration } from './glb-job-progress';
import ConverterTransformationSettingsForm from './ConverterTransformationSettingsForm';
import OptimizationPresetControls from './OptimizationPresetControls';
import {
  CONVERTER_JOB_PROFILE_GLB_IMPORT,
  buildConverterJobDebugReport,
  downloadConverterJobDebugReport,
} from './converter-job-diagnostics';
import {
  GLB_IMPORT_ROUTE,
  TRANSFORMATION_PROFILES,
  buildTransformationSettings,
  createTransformationFormDefaults,
} from './converter-transformation-settings';

const converterModelScaleLabelKeys = {
  1: 'x1',
  0.1: 'x0_1',
  0.01: 'x0_01',
  0.001: 'x0_001',
};

const defaultTargetBoundsMillimeters = {
  x: '',
  y: '',
  z: '',
};

const isAbortError = (error) => (
  error?.name === 'AbortError' || error?.code === 'ERR_CANCELED'
);

const ImportModelsModal = ({
  onCloseMenu,
  open,
  projectGuid,
  setOpen,
  sourceCalculationGuid,
  targetCompanyId,
}) => {
  const [localOpen, setLocalOpen] = useState(!!projectGuid);
  const isControlled = open !== undefined && setOpen !== undefined;
  const isModalOpen = isControlled ? open : localOpen;
  const setIsModalOpen = isControlled ? setOpen : setLocalOpen;

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [modelName, setModelName] = useState('');
  const [modelNameError, setModelNameError] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isStoredScaleLoading, setIsStoredScaleLoading] = useState(false);
  const [scale, setScale] = useState('1');
  const [rotationY, setRotationY] = useState(0);
  const [converterSettings, setConverterSettings] = useState(
    () => createTransformationFormDefaults(TRANSFORMATION_PROFILES.GLB_IMPORT),
  );
  const [converterSettingsErrors, setConverterSettingsErrors] = useState({});
  const [converterScaleMode, setConverterScaleMode] = useState(
    DEFAULT_GLB_CONVERTER_SCALE_MODE,
  );
  const [converterModelScale, setConverterModelScale] = useState(
    DEFAULT_GLB_CONVERTER_COEFFICIENT_SCALE,
  );
  const [converterCustomModelScale, setConverterCustomModelScale] = useState('');
  const [converterTargetBoundsMillimeters, setConverterTargetBoundsMillimeters] = useState(
    defaultTargetBoundsMillimeters,
  );
  const [converterDebugContext, setConverterDebugContext] = useState(null);
  const [converterDebugEntries, setConverterDebugEntries] = useState([]);
  const [converterError, setConverterError] = useState(null);
  const [converterJobId, setConverterJobId] = useState('');
  const [converterJobStatus, setConverterJobStatus] = useState(null);
  const [converterStatusNow, setConverterStatusNow] = useState(Date.now);
  const [converterLastStatusAt, setConverterLastStatusAt] = useState(null);
  const [pendingConverterRun, setPendingConverterRun] = useState(null);
  const { t } = useTranslation();
  const activeUploadAbortControllerRef = useRef(null);

  const handleScaleChange = (event) => setScale(event.target.value);
  const handleRotationChange = (event) => setRotationY(parseInt(event.target.value, 10));
  const handleConverterModelScaleChange = (event) => {
    const nextModelScale = event.target.value;

    setConverterModelScale(nextModelScale);

    if (nextModelScale !== CUSTOM_GLB_CONVERTER_MODEL_SCALE) {
      setConverterCustomModelScale('');
    }
  };
  const handleConverterTargetBoundsChange = (axisName) => (event) => {
    const nextValue = event.target.value;

    setConverterTargetBoundsMillimeters((currentBounds) => ({
      ...currentBounds,
      [axisName]: nextValue,
    }));
  };
  const handleConverterSettingsChange = (nextSettings) => {
    setConverterSettings(nextSettings);
    setConverterSettingsErrors({});

    if (!loading) {
      setUploadMessage('');
    }
  };

  const fileInputRef = useRef(null);
  const selectionState = getImportModelsSelectionState(selectedFiles);
  const isStoredSourceRetryMode = !!sourceCalculationGuid;
  const isConverterMode = Boolean(pendingConverterRun) ||
    selectionState.mode === 'converter' ||
    (isStoredSourceRetryMode && selectedFiles.length === 0);
  const canResumeConverterPolling = [
    GLB_PENDING_RUN_PHASES.POLLING,
    GLB_PENDING_RUN_PHASES.POLL_SUSPENDED,
  ].includes(pendingConverterRun?.phase);
  const isConverterCreationLocked = Boolean(
    pendingConverterRun && !canResumeConverterPolling,
  );
  const converterInputsDisabled = loading || Boolean(pendingConverterRun);
  const hasConverterDebugData = converterDebugEntries.length > 0;
  const converterWaitDuration = getGlbJobWaitDuration(
    converterJobStatus,
    converterStatusNow,
  );
  const isConverterStatusDelayed = loading && canResumeConverterPolling &&
    converterLastStatusAt !== null &&
    converterStatusNow - converterLastStatusAt >= CONVERTER_STATUS_DELAY_WARNING_MS;
  const converterStatusText = [
    isConverterStatusDelayed && t('importModels.messages.converterStatusDelayed'),
    loading && !isConverterStatusDelayed
      ? loadingText
      : canResumeConverterPolling
        ? converterJobStatus
          ? t('importModels.messages.converterLastKnownStatus', {
            status: getGlbJobStatusText(converterJobStatus, t),
          })
          : !isConverterStatusDelayed && t('importModels.messages.converterStatusReady')
        : '',
    converterJobId && t('importModels.labels.converterJobId', { jobId: converterJobId }),
    converterWaitDuration && t('importModels.labels.converterWaitDuration', {
      duration: converterWaitDuration,
    }),
  ].filter(Boolean).join('\n');
  const shouldRenderMenuItem = !projectGuid && !isControlled;
  const converterScaleModeOptions = GLB_CONVERTER_SCALE_MODES.map(
    (scaleModeValue) => ({
      label: t(`importModels.scaleModes.${scaleModeValue}`),
      value: scaleModeValue,
    }),
  );
  const converterModelScaleOptions = GLB_CONVERTER_MODEL_SCALE_PRESETS.map(
    (modelScaleValue) => ({
      label: t(`importModels.modelScales.${converterModelScaleLabelKeys[modelScaleValue]}`),
      value: modelScaleValue,
    }),
  );

  const abortActiveUpload = () => {
    activeUploadAbortControllerRef.current?.abort();
    activeUploadAbortControllerRef.current = null;
  };

  const appendConverterDebugEntry = ({ event, payload }) => {
    setConverterDebugEntries((currentEntries) => [
      ...currentEntries,
      buildConverterDebugEntry({
        event,
        payload,
      }),
    ]);
  };

  const resetConverterDebugData = () => {
    setConverterDebugContext(null);
    setConverterDebugEntries([]);
    setConverterError(null);
    setConverterJobId('');
    setConverterJobStatus(null);
    setConverterLastStatusAt(null);
  };

  const persistPendingConverterRun = (record) => {
    const persistedRecord = writeGlbPendingRun({
      projectGuid,
      record,
      sourceCalculationGuid,
    });

    setPendingConverterRun(persistedRecord);
    return persistedRecord;
  };

  const clearPendingConverterRun = () => {
    clearGlbPendingRun({
      projectGuid,
      sourceCalculationGuid,
    });
    setPendingConverterRun(null);
  };

  const downloadConverterDebugData = () => {
    if (!hasConverterDebugData) {
      return;
    }

    const latestStatusEntry = [...converterDebugEntries]
      .reverse()
      .find(({ event }) => event === 'job_status');
    const report = buildConverterJobDebugReport({
      error: converterError,
      jobId: converterJobId,
      latestStatus: latestStatusEntry?.payload,
      profile: CONVERTER_JOB_PROFILE_GLB_IMPORT,
      route: GLB_IMPORT_ROUTE,
      sourceContext: converterDebugContext,
      submittedSettings: converterDebugContext?.optimizationSettings,
      technicalEntries: converterDebugEntries,
    });

    downloadConverterJobDebugReport({
      fileNameOptions: {
        jobId: converterJobId,
        modelName,
        profile: CONVERTER_JOB_PROFILE_GLB_IMPORT,
      },
      report,
    });
  };

  useEffect(() => {
    if (!projectGuid) return;
    setLocalOpen(true);
  }, [projectGuid]);

  useEffect(() => {
    if (!isModalOpen || !loading || !canResumeConverterPolling) {
      return undefined;
    }

    const timer = window.setInterval(() => setConverterStatusNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isModalOpen, loading, canResumeConverterPolling]);

  useEffect(() => {
    if (selectedFiles.length > 0) {
      const defaultName = getDefaultModelName(selectedFiles);

      if (defaultName) {
        setModelName(defaultName);
      }
    }
  }, [selectedFiles]);

  useEffect(() => {
    if (!isModalOpen || pendingConverterRun) {
      return;
    }

    setConverterSettings(
      createTransformationFormDefaults(TRANSFORMATION_PROFILES.GLB_IMPORT),
    );
    setConverterSettingsErrors({});
    setConverterScaleMode(DEFAULT_GLB_CONVERTER_SCALE_MODE);
    setConverterModelScale(DEFAULT_GLB_CONVERTER_COEFFICIENT_SCALE);
    setConverterCustomModelScale('');
    setConverterTargetBoundsMillimeters(defaultTargetBoundsMillimeters);
    setIsStoredScaleLoading(false);
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen || pendingConverterRun) {
      return;
    }

    const restoredRun = readGlbPendingRun({
      projectGuid,
      sourceCalculationGuid,
    });

    if (!restoredRun) {
      return;
    }

    setPendingConverterRun(restoredRun);
    setConverterJobId(restoredRun.jobId);
    setIsStoredScaleLoading(false);
    setModelName(restoredRun.modelName ?? '');
    setConverterDebugContext(restoredRun.debugContext ?? null);
    setConverterDebugEntries([
      buildConverterDebugEntry({
        event: 'job_ownership_restored',
        payload: {
          jobId: restoredRun.jobId,
          phase: restoredRun.phase,
        },
      }),
    ]);

    if (restoredRun.formSettings) {
      setConverterSettings(restoredRun.formSettings);
    }

    if (restoredRun.scaleForm) {
      setConverterScaleMode(restoredRun.scaleForm.scaleMode);
      setConverterModelScale(restoredRun.scaleForm.modelScale);
      setConverterCustomModelScale(restoredRun.scaleForm.customModelScale ?? '');
      setConverterTargetBoundsMillimeters(
        restoredRun.scaleForm.targetBoundsMillimeters ?? defaultTargetBoundsMillimeters,
      );
    }

    if (restoredRun.phase === GLB_PENDING_RUN_PHASES.PUBLISH_UNKNOWN) {
      setUploadMessage(t('importModels.errors.converterPublishUnknown'));
      setLoadingText('');
      return;
    }

    if (restoredRun.phase === GLB_PENDING_RUN_PHASES.LEGACY_CLAIMED) {
      setUploadMessage(t('importModels.errors.converterFinalizationUnknown'));
      setLoadingText('');
      return;
    }

    setUploadMessage('');
    setLoadingText(t('importModels.messages.converterStatusReady'));
  }, [
    isModalOpen,
    pendingConverterRun,
    projectGuid,
    sourceCalculationGuid,
    t,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isStoredSourceRetryMode) {
      return;
    }

    setModelName((currentModelName) => (
      currentModelName.trim()
        ? currentModelName
        : t('importModels.defaults.storedSourceModelName')
    ));
  }, [isModalOpen, isStoredSourceRetryMode, t]);

  useEffect(() => {
    if (
      !isModalOpen ||
      !isStoredSourceRetryMode ||
      pendingConverterRun ||
      readGlbPendingRun({ projectGuid, sourceCalculationGuid })
    ) {
      return undefined;
    }

    const abortController = new AbortController();
    setIsStoredScaleLoading(true);

    const loadStoredScaleSettings = async () => {
      try {
        const modelTransformReport = await downloadStoredModelTransformReport({
          calculationGuid: sourceCalculationGuid,
          signal: abortController.signal,
        });
        const retryScaleSettings = buildConverterRetryScaleSettings(modelTransformReport);

        setConverterScaleMode(retryScaleSettings.scaleMode);
        setConverterModelScale(retryScaleSettings.modelScale);
        setConverterCustomModelScale(retryScaleSettings.customModelScale);
        setConverterTargetBoundsMillimeters(retryScaleSettings.targetBoundsMillimeters);
      } catch (error) {
        if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          return;
        }

        console.warn('Could not load GLB retry scale settings:', error);
      } finally {
        if (!abortController.signal.aborted) {
          setIsStoredScaleLoading(false);
        }
      }
    };

    loadStoredScaleSettings();

    return () => {
      abortController.abort();
    };
  }, [
    isModalOpen,
    isStoredSourceRetryMode,
    pendingConverterRun,
    projectGuid,
    sourceCalculationGuid,
  ]);

  useEffect(() => () => {
    abortActiveUpload();
  }, []);

  const selectFiles = (files) => {
    if (pendingConverterRun) {
      return;
    }

    const nextSelectionState = getImportModelsSelectionState(files);

    abortActiveUpload();
    setUploadMessage('');
    setLoading(false);
    setLoadingText('');
    setIsUploadComplete(false);
    setUploadProgress(0);
    setModelNameError('');
    resetConverterDebugData();

    if (isStoredSourceRetryMode && files.length > 0 && nextSelectionState.mode !== 'converter') {
      setSelectedFiles([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      setUploadMessage(t('importModels.errors.singleGlbOnly'));
      return;
    }

    if (nextSelectionState.errorMessageKey) {
      setSelectedFiles([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      setUploadMessage(
        t(nextSelectionState.errorMessageKey, nextSelectionState.errorMessageValues),
      );
      return;
    }

    if (files.length > 0) {
      setSelectedFiles(files);
      return;
    }

    setSelectedFiles([]);
  };

  const handleFileChange = (event) => {
    selectFiles(Array.from(event.target.files || []));
  };

  const handleFileDrop = (event) => {
    event.preventDefault();

    if (loading || pendingConverterRun) {
      return;
    }

    selectFiles(Array.from(event.dataTransfer.files || []));
  };

  const uploadLegacyFiles = async () => {
    const storage = localStorage.getItem('company');
    const storageCompany = JSON.parse(storage || 'null');

    setLoading(true);
    setUploadMessage('');
    setLoadingText(t('importModels.labels.progress', { percent: 0 }));
    setUploadProgress(0);
    setIsUploadComplete(false);

    const formData = new FormData();
    formData.append('IdCompany', storageCompany?.id || 1);
    formData.append('name', modelName);
    formData.append('Scale', scale);
    formData.append('RotationY', rotationY);
    if (projectGuid) formData.append('ProjectGuid', projectGuid);
    selectedFiles.forEach((file) => formData.append('Files', file));

    const maxRetries = 3;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        await axios.post(`${globalConfig.config.common.api}Projects/ImportModels`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(progress);
              setLoadingText(t('importModels.labels.progress', { percent: progress }));
            }
          },
        });

        setUploadMessage(
          <>
            {t('importModels.messages.allFilesUploaded')}{' '}
            {t('importModels.messages.chatWillBeCreatedWithImage')}{' '}
            <img
              src={mainIcon}
              alt="Conversion Icon"
              style={{ width: 50, height: 50, verticalAlign: 'sub', margin: '0 5px' }}
            />{' '}
            {t('importModels.messages.importProcessShown')}{' '}
            {t('importModels.messages.resultInSameChat')}
          </>
        );
        setSelectedFiles([]);
        setModelName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setIsUploadComplete(true);
        return;
      } catch (error) {
        attempt += 1;
        console.error(`Attempt ${attempt} failed:`, error);
        setUploadMessage(
          attempt < maxRetries
            ? t('importModels.messages.retryAttempt', { attempt })
            : t('importModels.messages.uploadError')
        );
      } finally {
        setLoading(false);
        setLoadingText('');
      }
    }
  };

  const uploadConverterFile = async (scaleRequest, resumeRun = null) => {
    const abortController = new AbortController();
    const isResuming = Boolean(resumeRun);
    let currentPendingRun = resumeRun;
    let selectedFile = isResuming ? null : selectedFiles[0];

    activeUploadAbortControllerRef.current = abortController;

    setLoading(true);
    setUploadMessage('');
    setLoadingText(
      isResuming
        ? t('importModels.messages.converterCheckingStatus')
        : t('importModels.messages.converterUploading', { percent: 0 }),
    );
    setUploadProgress(0);
    setIsUploadComplete(false);
    setConverterError(null);

    if (isResuming) {
      setConverterJobId(resumeRun.jobId);
      appendConverterDebugEntry({
        event: 'job_status_polling_resumed',
        payload: {
          jobId: resumeRun.jobId,
        },
      });
    } else {
      setConverterJobId('');
      setConverterJobStatus(null);
      setConverterDebugContext({
        existingProjectGuid: projectGuid ?? null,
        flow: isStoredSourceRetryMode
          ? 'stored_source_retry'
          : projectGuid
            ? 'retry_variant'
            : 'new_project',
        modelName: modelName.trim() || null,
        sourceCalculationGuid: sourceCalculationGuid ?? null,
        startedAt: new Date().toISOString(),
      });
      setConverterDebugEntries([
        buildConverterDebugEntry({
          event: 'flow_started',
          payload: {
            modelName: modelName.trim() || null,
            sourceCalculationGuid: sourceCalculationGuid ?? null,
          },
        }),
      ]);
    }

    try {
      const accessToken = readAccessTokenFromStorage();
      const companyId = currentPendingRun?.companyId ??
        activateConverterTargetCompany({ targetCompanyId });

      if (!isResuming && !selectedFile && isStoredSourceRetryMode) {
        setLoadingText(t('importModels.messages.converterSourceDownloading'));
        selectedFile = await downloadStoredGlbSourceFile({
          calculationGuid: sourceCalculationGuid,
          signal: abortController.signal,
        });
      }

      const effectiveScaleRequest = currentPendingRun?.scaleRequest ?? scaleRequest;
      const effectiveFormSettings = currentPendingRun?.formSettings ?? converterSettings;
      const trimmedModelName = currentPendingRun?.modelName ?? (
        modelName.trim() ||
        getDefaultModelName([selectedFile]) ||
        t('importModels.defaults.storedSourceModelName')
      );
      const screenshotOptions = buildConverterScreenshotOptions({
        renderOutputMode: effectiveFormSettings.renderOutputMode,
      });
      const optimizationSettings = currentPendingRun?.optimizationSettings ??
        buildTransformationSettings({
          profile: TRANSFORMATION_PROFILES.GLB_IMPORT,
          values: effectiveFormSettings,
        });
      const debugContext = currentPendingRun?.debugContext ?? {
        apiBaseUrl: globalConfig.config.common.api,
        converterBaseUrl: globalConfig.config.common.glbConverter,
        existingProjectGuid: projectGuid ?? null,
        file: {
          lastModified: selectedFile?.lastModified ?? null,
          name: selectedFile?.name ?? null,
          size: selectedFile?.size ?? null,
          type: selectedFile?.type ?? null,
        },
        flow: isStoredSourceRetryMode
          ? 'stored_source_retry'
          : projectGuid
            ? 'retry_variant'
            : 'new_project',
        modelScale: effectiveScaleRequest.modelScale ?? null,
        modelScaleMode: effectiveScaleRequest.scaleMode,
        modelTargetBounds: effectiveScaleRequest.modelTargetBounds ?? null,
        modelName: trimmedModelName,
        optimizationSettings,
        renderOutputMode: effectiveFormSettings.renderOutputMode,
        screenshotOptions,
        sourceCalculationGuid: sourceCalculationGuid ?? null,
        startedAt: new Date().toISOString(),
      };

      setConverterDebugContext(debugContext);

      if (!isResuming) {
        setConverterDebugEntries([
          buildConverterDebugEntry({
            event: 'upload_started',
            payload: {
              modelScale: effectiveScaleRequest.modelScale ?? null,
              modelScaleMode: effectiveScaleRequest.scaleMode,
              modelTargetBounds: effectiveScaleRequest.modelTargetBounds ?? null,
              modelName: trimmedModelName,
              optimizationSettings,
              renderOutputMode: effectiveFormSettings.renderOutputMode,
              screenshotOptions,
              sourceCalculationGuid: sourceCalculationGuid ?? null,
            },
          }),
        ]);
        appendConverterDebugEntry({
          event: 'preflight_resolved',
          payload: { companyId },
        });
      }

      let jobId = currentPendingRun?.jobId;

      if (!jobId) {
        const createJobResult = await createGlbConversionJob({
          accessToken,
          file: selectedFile,
          idCompany: companyId,
          modelName: trimmedModelName,
          modelScale: effectiveScaleRequest.modelScale,
          modelTargetBounds: effectiveScaleRequest.modelTargetBounds,
          onUploadProgress: (progress) => {
            setUploadProgress(progress);
            setLoadingText(t('importModels.messages.converterUploading', { percent: progress }));
          },
          optimizationSettings,
          projectGuid,
          renderOutputMode: effectiveFormSettings.renderOutputMode,
          signal: abortController.signal,
        });
        jobId = createJobResult.jobId;
        currentPendingRun = persistPendingConverterRun({
          companyId,
          createdAt: new Date().toISOString(),
          debugContext,
          formSettings: effectiveFormSettings,
          jobId,
          modelName: trimmedModelName,
          optimizationSettings,
          phase: GLB_PENDING_RUN_PHASES.POLLING,
          scaleForm: {
            customModelScale: converterCustomModelScale,
            modelScale: converterModelScale,
            scaleMode: converterScaleMode,
            targetBoundsMillimeters: converterTargetBoundsMillimeters,
          },
          scaleRequest: effectiveScaleRequest,
        });

        setConverterJobId(jobId);
        setConverterJobStatus({ status: 'queued', createdAt: currentPendingRun.createdAt });
        setConverterStatusNow(Date.now());
        appendConverterDebugEntry({
          event: 'job_created',
          payload: {
            ...createJobResult,
            modelScale: effectiveScaleRequest.modelScale ?? null,
            modelScaleMode: effectiveScaleRequest.scaleMode,
            modelTargetBounds: effectiveScaleRequest.modelTargetBounds ?? null,
            optimizationSettings,
            renderOutputMode: effectiveFormSettings.renderOutputMode,
            screenshotOptions,
          },
        });
      }

      if (!isResuming) {
        setLoadingText(t('importModels.messages.converterQueued'));
      }

      setConverterLastStatusAt(Date.now());
      setConverterStatusNow(Date.now());
      const jobStatus = await pollGlbConversionJob({
        accessToken,
        getAccessToken: readAccessTokenFromStorage,
        jobId,
        onRetryableError: ({ attempt, elapsedSinceLastStatusMs, error }) => {
          appendConverterDebugEntry({
            event: 'job_status_retry_failed',
            payload: {
              attempt,
              elapsedSinceLastStatusMs,
              error: buildConverterDebugErrorPayload(error),
            },
          });
        },
        onStatusChange: (currentJobStatus) => {
          setConverterJobStatus(currentJobStatus);
          setConverterLastStatusAt(Date.now());
          setConverterStatusNow(Date.now());
          appendConverterDebugEntry({
            event: 'job_status',
            payload: currentJobStatus,
          });

          setLoadingText(getGlbJobStatusText(currentJobStatus, t));
        },
        signal: abortController.signal,
      });

      currentPendingRun = persistPendingConverterRun({
        ...currentPendingRun,
        phase: GLB_PENDING_RUN_PHASES.LEGACY_CLAIMED,
      });

      const calculationGuid = extractLegacyCalculationGuid(jobStatus);
      appendConverterDebugEntry({
        event: 'model_scale_resolved',
        payload: jobStatus?.modelScale ?? null,
      });
      appendConverterDebugEntry({
        event: 'mesh_correspondence_resolved',
        payload: jobStatus?.meshCorrespondence ?? null,
      });
      let redirectTarget;

      if (projectGuid) {
        redirectTarget = resolveConverterRedirectTarget({
          calculationGuid,
          existingProjectGuid: projectGuid,
        });
        appendConverterDebugEntry({
          event: 'retry_project_reused',
          payload: {
            calculationGuid,
            projectGuid: redirectTarget.projectGuid,
            variantGuid: redirectTarget.variantGuid,
          },
        });
      } else {
        setLoadingText(t('importModels.messages.converterCreatingChat'));

        let displayTapeResult;

        try {
          displayTapeResult = await createDisplayTapeFromCalculation({
            calculationGuid,
            idCompany: companyId,
            signal: abortController.signal,
          });
          appendConverterDebugEntry({
            event: 'display_tape_created',
            payload: {
              calculationGuid,
              displayTapeGuid: displayTapeResult.displayTapeGuid,
              responseData: displayTapeResult.responseData,
            },
          });
        } catch (error) {
          appendConverterDebugEntry({
            event: 'display_tape_create_failed',
            payload: {
              calculationGuid,
              error: buildConverterDebugErrorPayload(error),
            },
          });
          throw error;
        }

        try {
          const chatProjectResult = await createChatProjectFromDisplayTape({
            displayTapeGuid: displayTapeResult.displayTapeGuid,
            idCompany: companyId,
            modelName: trimmedModelName,
            signal: abortController.signal,
          });

          redirectTarget = resolveConverterRedirectTarget({
            calculationGuid,
            createdProjectResponseData: chatProjectResult.responseData,
          });
          appendConverterDebugEntry({
            event: 'chat_project_created',
            payload: {
              calculationGuid,
              displayTapeGuid: displayTapeResult.displayTapeGuid,
              projectGuid: redirectTarget.projectGuid,
              requestData: chatProjectResult.requestData,
              responseData: chatProjectResult.responseData,
              variantGuid: redirectTarget.variantGuid,
            },
          });
        } catch (error) {
          appendConverterDebugEntry({
            event: 'chat_project_create_failed',
            payload: {
              calculationGuid,
              displayTapeGuid: displayTapeResult.displayTapeGuid,
              error: buildConverterDebugErrorPayload(error),
            },
          });
          throw error;
        }
      }

      const conversionAuditDelivery = await sendBestEffortConversionAuditMessage({
        calculationGuid: resolveGlbConversionAuditCalculationGuid({
          calculationGuid,
          redirectTarget,
        }),
        conversionAudit: jobStatus?.conversionAudit,
        isAbortError,
        projectGuid: redirectTarget.projectGuid,
        signal: abortController.signal,
      });

      if (conversionAuditDelivery.status === 'missing') {
        appendConverterDebugEntry({
          event: 'conversion_audit_message_missing',
          payload: {
            jobId,
            status: jobStatus?.status,
          },
        });
      } else if (conversionAuditDelivery.status === 'failed') {
        appendConverterDebugEntry({
          event: 'conversion_audit_message_failed',
          payload: buildConverterDebugErrorPayload(conversionAuditDelivery.error),
        });
      } else {
        appendConverterDebugEntry({
          event: 'conversion_audit_message_sent',
          payload: {
            projectGuid: redirectTarget.projectGuid,
            responseData: conversionAuditDelivery.responseData,
            variantGuid: redirectTarget.variantGuid,
          },
        });
      }

      try {
        const sourceLinkMessageResult = await sendGlbSourceLinkMessage({
          calculationGuid,
          projectGuid: redirectTarget.projectGuid,
          signal: abortController.signal,
        });

        appendConverterDebugEntry({
          event: 'source_link_message_sent',
          payload: {
            projectGuid: redirectTarget.projectGuid,
            responseData: sourceLinkMessageResult.responseData,
            sourceFile: sourceLinkMessageResult.sourceFile,
            variantGuid: redirectTarget.variantGuid,
          },
        });
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }

        appendConverterDebugEntry({
          event: 'source_link_message_failed',
          payload: buildConverterDebugErrorPayload(error),
        });

        try {
          const sourceLinkFallbackMessageResponse = await sendGlbSourceLinkFallbackMessage({
            calculationGuid,
            projectGuid: redirectTarget.projectGuid,
            signal: abortController.signal,
          });

          appendConverterDebugEntry({
            event: 'source_link_fallback_message_sent',
            payload: {
              projectGuid: redirectTarget.projectGuid,
              responseData: sourceLinkFallbackMessageResponse,
              variantGuid: redirectTarget.variantGuid,
            },
          });
        } catch (fallbackError) {
          if (isAbortError(fallbackError)) {
            throw fallbackError;
          }

          appendConverterDebugEntry({
            event: 'source_link_fallback_message_failed',
            payload: buildConverterDebugErrorPayload(fallbackError),
          });
        }
      }

      try {
        const retryActionMessageResponse = await sendGlbConversionRetryActionMessage({
          calculationGuid,
          projectGuid: redirectTarget.projectGuid,
          signal: abortController.signal,
        });

        appendConverterDebugEntry({
          event: 'retry_action_message_sent',
          payload: {
            projectGuid: redirectTarget.projectGuid,
            responseData: retryActionMessageResponse,
            variantGuid: redirectTarget.variantGuid,
          },
        });
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }

        appendConverterDebugEntry({
          event: 'retry_action_message_failed',
          payload: buildConverterDebugErrorPayload(error),
        });
      }

      if (abortController.signal.aborted) {
        return;
      }

      setLoadingText(t('importModels.messages.converterRedirecting'));

      appendConverterDebugEntry({
        event: 'redirect_requested',
        payload: {
          calculationGuid,
          projectGuid: redirectTarget.projectGuid,
          url: `/app?pr=${redirectTarget.projectGuid}&variant=${redirectTarget.variantGuid}`,
          variantGuid: redirectTarget.variantGuid,
        },
      });
      clearPendingConverterRun();
      window.location.assign(
        `/app?pr=${redirectTarget.projectGuid}&variant=${redirectTarget.variantGuid}`,
      );
    } catch (error) {
      if (isAbortError(error)) {
        if (activeUploadAbortControllerRef.current === abortController) {
          appendConverterDebugEntry({
            event: 'flow_aborted',
            payload: {
              reason: 'user_cancelled',
            },
          });
        }
        return;
      }

      if (activeUploadAbortControllerRef.current !== abortController) {
        return;
      }

      console.error('GLB converter upload failed:', error);

      if (error?.messageKey === 'importModels.errors.converterJobFailed') {
        clearPendingConverterRun();
        currentPendingRun = null;
      } else if (currentPendingRun) {
        const nextPhase = currentPendingRun.phase === GLB_PENDING_RUN_PHASES.LEGACY_CLAIMED
          ? GLB_PENDING_RUN_PHASES.LEGACY_CLAIMED
          : error?.messageKey === 'importModels.errors.converterPublishUnknown'
            ? GLB_PENDING_RUN_PHASES.PUBLISH_UNKNOWN
            : GLB_PENDING_RUN_PHASES.POLL_SUSPENDED;

        currentPendingRun = persistPendingConverterRun({
          ...currentPendingRun,
          phase: nextPhase,
        });
      }

      setConverterError(error);
      appendConverterDebugEntry({
        event: 'flow_failed',
        payload: buildConverterDebugErrorPayload(error),
      });
      setUploadMessage(
        buildConverterErrorMessage({
          error,
          translate: t,
        }),
      );
    } finally {
      if (activeUploadAbortControllerRef.current === abortController) {
        activeUploadAbortControllerRef.current = null;
        setLoading(false);
        setLoadingText('');
      }
    }
  };

  const uploadFiles = async () => {
    if (pendingConverterRun) {
      if (canResumeConverterPolling) {
        await uploadConverterFile(
          pendingConverterRun.scaleRequest,
          pendingConverterRun,
        );
      }

      return;
    }

    if (isStoredScaleLoading) {
      return;
    }

    if (selectedFiles.length === 0 && !isStoredSourceRetryMode) {
      setUploadMessage(t('importModels.messages.chooseFiles'));
      return;
    }

    if (isStoredSourceRetryMode && selectedFiles.length > 0 && selectionState.mode !== 'converter') {
      setUploadMessage(t('importModels.errors.singleGlbOnly'));
      return;
    }

    if (selectionState.errorMessageKey) {
      setUploadMessage(t(selectionState.errorMessageKey, selectionState.errorMessageValues));
      return;
    }

    if (!modelName.trim() && !isStoredSourceRetryMode) {
      setModelNameError(t('importModels.errors.nameRequired'));
      return;
    }

    if (isConverterMode) {
      let scaleRequest;

      try {
        buildTransformationSettings({
          profile: TRANSFORMATION_PROFILES.GLB_IMPORT,
          values: converterSettings,
        });
        scaleRequest = resolveConverterScaleRequest({
          customModelScale: converterCustomModelScale,
          modelScale: converterModelScale,
          scaleMode: converterScaleMode,
          targetBoundsMillimeters: converterTargetBoundsMillimeters,
        });
      } catch (error) {
        if (error?.field) {
          setConverterSettingsErrors({
            [error.field]: t('converterTransformation.validation.invalidValue'),
          });
          setUploadMessage(
            t('converterTransformation.validation.invalidSettings'),
          );
          return;
        }

        setUploadMessage(
          buildConverterErrorMessage({
            error,
            translate: t,
          }),
        );
        return;
      }

      await uploadConverterFile(scaleRequest);
      return;
    }

    await uploadLegacyFiles();
  };

  const handleOpen = () => setIsModalOpen(true);

  const handleClose = () => {
    abortActiveUpload();
    setIsModalOpen(false);
    if (isUploadComplete || projectGuid) onCloseMenu();

    if (pendingConverterRun) {
      setLoading(false);
      setIsStoredScaleLoading(false);
      return;
    }

    setUploadMessage('');
    setSelectedFiles([]);
    setModelName('');
    setUploadProgress(0);
    setLoading(false);
    setLoadingText('');
    setIsUploadComplete(false);
    setModelNameError('');
    setConverterSettings(
      createTransformationFormDefaults(TRANSFORMATION_PROFILES.GLB_IMPORT),
    );
    setConverterSettingsErrors({});
    setConverterScaleMode(DEFAULT_GLB_CONVERTER_SCALE_MODE);
    setConverterModelScale(DEFAULT_GLB_CONVERTER_COEFFICIENT_SCALE);
    setConverterCustomModelScale('');
    setConverterTargetBoundsMillimeters(defaultTargetBoundsMillimeters);
    setIsStoredScaleLoading(false);
    resetConverterDebugData();

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        closeAfterTransition
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Fade in={isModalOpen}>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.paper',
              boxShadow: 5,
              p: { xs: 2.5, sm: 4 },
              borderRadius: 2,
              width: { xs: 'calc(100vw - 32px)', sm: '720px' },
              maxWidth: '100%',
              maxHeight: 'calc(100vh - 32px)',
              overflow: 'hidden',
              mx: 'auto',
              my: 'auto',
              boxSizing: 'border-box',
            }}>
            <IconButton
              aria-label={t('importModels.a11y.close')}
              onClick={handleClose}
              sx={{ position: 'absolute', right: 10, top: 10, color: '#999' }}
            >
              <CloseIcon />
            </IconButton>

            <Box sx={{ overflowY: 'auto', minHeight: 0, pr: 0.5 }}>
              <div className="infoList--title">{t('menu.importModels')}</div>

              <TextField
                disabled={Boolean(pendingConverterRun)}
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                error={!!modelNameError}
                helperText={modelNameError}
                fullWidth
                required
                placeholder={t('importModels.placeholders.modelName')}
                sx={{ mb: 2.5, mt: 2 }}
              />

              {isStoredSourceRetryMode && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mb: 1 }}
                  >
                    {t('importModels.messages.storedSourceRetryHint')}
                  </Typography>
                  {isStoredScaleLoading && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'block', mb: 2 }}
                    >
                      {t('converterTransformation.source.loadingSettings')}
                    </Typography>
                  )}
                </>
              )}

              <Box
                onClick={() => {
                  if (!converterInputsDisabled) {
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleFileDrop}
                onKeyDown={(event) => {
                  if (!converterInputsDisabled && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                role="button"
                tabIndex={converterInputsDisabled ? -1 : 0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: converterInputsDisabled ? 'default' : 'pointer',
                  mb: 2.5,
                  opacity: converterInputsDisabled ? 0.6 : 1,
                  p: 2,
                  textAlign: 'center',
                }}
              >
                <input
                  type="file"
                  accept={isStoredSourceRetryMode ? '.glb,model/gltf-binary' : undefined}
                  disabled={converterInputsDisabled}
                  multiple={!isStoredSourceRetryMode}
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                />
                <Typography variant="body2">
                  {pendingConverterRun?.debugContext?.file?.name ||
                    (selectedFiles.length > 0
                    ? selectedFiles.map(({ name }) => name).join(', ')
                    : t('converterTransformation.source.dropGlb'))}
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  {t('converterTransformation.source.chooseGlb')}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 2, mt: -1, color: 'text.secondary' }}
              >
                {t('importModels.labels.converterFileLimit', {
                  maxSizeMb: GLB_CONVERTER_MAX_UPLOAD_SIZE_MB,
                })}
              </Typography>

              {isConverterMode && (
                <>
                  <FormControl
                    disabled={converterInputsDisabled || isStoredScaleLoading}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    <InputLabel>{t('importModels.labels.scaleMode')}</InputLabel>
                    <Select
                      value={converterScaleMode}
                      label={t('importModels.labels.scaleMode')}
                      onChange={(event) => setConverterScaleMode(event.target.value)}
                    >
                      {converterScaleModeOptions.map((scaleModeOption) => (
                        <MenuItem key={scaleModeOption.value} value={scaleModeOption.value}>
                          {scaleModeOption.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {converterScaleMode === 'coefficient' && (
                    <>
                      <FormControl
                        disabled={converterInputsDisabled || isStoredScaleLoading}
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        <InputLabel>{t('importModels.labels.modelScaleCoefficient')}</InputLabel>
                        <Select
                          value={converterModelScale}
                          label={t('importModels.labels.modelScaleCoefficient')}
                          onChange={handleConverterModelScaleChange}
                        >
                          {converterModelScaleOptions.map((modelScaleOption) => (
                            <MenuItem key={modelScaleOption.value} value={modelScaleOption.value}>
                              {modelScaleOption.label}
                            </MenuItem>
                          ))}
                          <MenuItem value={CUSTOM_GLB_CONVERTER_MODEL_SCALE}>
                            {t('importModels.modelScales.custom')}
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {converterModelScale === CUSTOM_GLB_CONVERTER_MODEL_SCALE && (
                        <TextField
                          disabled={converterInputsDisabled || isStoredScaleLoading}
                          value={converterCustomModelScale}
                          onChange={(event) => setConverterCustomModelScale(event.target.value)}
                          fullWidth
                          label={t('importModels.labels.modelScaleCustom')}
                          helperText={t('importModels.labels.modelScaleCustomHelp')}
                          placeholder="0.01"
                          autoComplete="off"
                          sx={{ mb: 2 }}
                        />
                      )}
                    </>
                  )}

                  {converterScaleMode === 'bounds' && (
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                        mb: 2,
                      }}
                    >
                      {['x', 'y', 'z'].map((axisName) => (
                        <TextField
                          disabled={converterInputsDisabled || isStoredScaleLoading}
                          key={axisName}
                          value={converterTargetBoundsMillimeters[axisName]}
                          onChange={handleConverterTargetBoundsChange(axisName)}
                          fullWidth
                          label={t(`importModels.labels.modelTargetBounds.${axisName}`)}
                          helperText={t('importModels.labels.modelTargetBoundsUnit')}
                          placeholder="1000"
                          autoComplete="off"
                        />
                      ))}
                    </Box>
                  )}

                  <OptimizationPresetControls
                    active={isModalOpen && isConverterMode}
                    disabled={converterInputsDisabled}
                    onChange={handleConverterSettingsChange}
                    profile={TRANSFORMATION_PROFILES.GLB_IMPORT}
                    value={converterSettings}
                  />

                  <ConverterTransformationSettingsForm
                    disabled={converterInputsDisabled}
                    errors={converterSettingsErrors}
                    onChange={handleConverterSettingsChange}
                    profile={TRANSFORMATION_PROFILES.GLB_IMPORT}
                    value={converterSettings}
                  />
                </>
              )}

              {!isConverterMode && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ fontSize: '1.2rem' }}>
                      {t('importModels.labels.scale')}
                    </InputLabel>
                    <Select
                      value={scale}
                      label={t('importModels.labels.scale')}
                      onChange={handleScaleChange}
                    >
                      <MenuItem value="1">1:1</MenuItem>
                      <MenuItem value="10">1:10</MenuItem>
                      <MenuItem value="100">1:100</MenuItem>
                      <MenuItem value="1000">1:1000</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('importModels.labels.rotation')}</InputLabel>
                    <Select
                      value={rotationY}
                      label={t('importModels.labels.rotation')}
                      onChange={handleRotationChange}
                    >
                      <MenuItem value={0}>0°</MenuItem>
                      <MenuItem value={90}>90°</MenuItem>
                      <MenuItem value={180}>180°</MenuItem>
                      <MenuItem value={270}>270°</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}

              {isConverterMode && (
                <ConverterJobRunPanel
                  errorMessage={!loading ? uploadMessage : ''}
                  isRunning={loading}
                  labels={{
                    downloadDebug: t('converterTransformation.job.downloadDebug'),
                    progress: t('converterTransformation.job.status'),
                    technicalDetails: t('converterTransformation.job.technicalLog'),
                  }}
                  onDownloadDebug={
                    hasConverterDebugData
                      ? downloadConverterDebugData
                      : undefined
                  }
                  statusText={converterStatusText}
                  technicalEntries={converterDebugEntries.filter(({ event }) => (
                    event === 'job_created' ||
                    event === 'job_ownership_restored' ||
                    event === 'job_status' ||
                    event === 'job_status_polling_resumed' ||
                    event === 'job_status_retry_failed' ||
                    event === 'flow_failed'
                  ))}
                  uploadProgress={
                    loading && !converterJobId
                      ? uploadProgress
                      : undefined
                  }
                />
              )}

              {!isConverterMode && uploadMessage && (
                <Typography
                  component="div"
                  sx={{
                    mt: 2,
                    maxHeight: { xs: '30vh', sm: 240 },
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    overflowWrap: 'anywhere',
                    pr: 1,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {uploadMessage}
                </Typography>
              )}

              {!isConverterMode && loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CircularProgress size={24} />
                  <Typography sx={{ ml: 2 }}>{loadingText}</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
              <button
                onClick={isUploadComplete ? handleClose : uploadFiles}
                disabled={loading || isStoredScaleLoading || isConverterCreationLocked}
              >
                {isUploadComplete
                  ? t('importModels.buttons.ok')
                  : canResumeConverterPolling
                    ? t('importModels.buttons.checkStatus')
                    : t('importModels.buttons.import')}
              </button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {shouldRenderMenuItem && (
        <div className="bm-item menu-item" onClick={handleOpen}>
          {t('menu.importModels')}
        </div>
      )}
    </>
  );
};

export default ImportModelsModal;
